<template>
  <SSection :desktop-full="true" :title="t('views.cottage.owner.title')">
    <TAvatar
      :img-prop="imgProp"
      size="lg"
      variant="image"
      :title="item?.owner.name"
      class="mt-8 mb-4"
    />

    <TProse :responsive="false" :clear="false">
      <p>{{ item?.owner.description }}</p>
      <ul>
        <li v-if="calendarUpdate">
          {{ calendarUpdate }}
        </li>
        <li v-if="item.owner.acceptanceRate">
          {{
            t('views.cottage.owner.acceptanceRate', {
              rate: item.owner.acceptanceRate,
            })
          }}
        </li>
        <li v-if="item.owner.answer && item.owner.answer.rate !== null">
          {{
            item.owner.answer.rate > 0
              ? t('views.cottage.owner.answerRate', {
                  rate: item.owner.answer.rate,
                  range: t(`range.${item.owner.answer.range}`),
                  value: item.owner.answer.value,
                  unit: t(
                    `time.${item.owner.answer.unit}`,
                    item.owner.answer.value,
                  ),
                })
              : t('views.cottage.owner.noAnswerRate')
          }}
        </li>
        <li v-if="languages">
          {{ t('views.cottage.owner.languages') }}: {{ languages }}.
        </li>
      </ul>
    </TProse>

    <div class="flex flex-col mt-3 gap-y-3 gap-x-4 lg:flex-row">
      <UButton
        :ui="mobileFullWidthButton"
        color="gray"
        variant="solid"
        size="sm"
        :label="t('views.cottage.owner.sendMessage')"
        icon="i-heroicons-envelope"
        :to="goToCottageContact"
        rel="nofollow"
        external
      />
      <UButton
        v-if="item.salesService.isAllowedToShowPhone"
        :ui="mobileFullWidthButton"
        color="gray"
        variant="solid"
        size="sm"
        :label="t('views.cottage.owner.showPhone')"
        icon="i-heroicons-phone"
        :to="goToCottagePhones"
        rel="nofollow"
        external
      />
      <UButton
        v-if="item.salesService.isAllowedToShowWhatsapp"
        :ui="mobileFullWidthButton"
        color="gray"
        variant="solid"
        size="sm"
        :label="t('views.cottage.owner.sendWhatsapp')"
        icon="i-ic-baseline-whatsapp"
        :to="goToCottageContactWhatsApp"
        rel="nofollow"
        external
      />
    </div>
  </SSection>
</template>

<script setup lang="ts">
import type { CottagePage } from '../../entities/CottagePage'
import type { TLazyimg_Props } from '~/modules/design-system/components/t-lazyimg/constants/types'

import { formatCottageCalendarUpdate } from '../../utils/formatCottageCalendarUpdate'
import { addConnectorsToList } from '~/modules/lang/composables/useLanguageFormatter'

import { mobileFullWidthButton } from '~/modules/design-system/utils/nuxt-ui-config/components/button'

const route = useRoute()

type itemType = Pick<CottagePage, 'owner' | 'calendarUpdate'> & {
  features: Pick<CottagePage['features'], 'languages'>
} & {
  salesService: Pick<
    CottagePage['salesService'],
    'isAllowedToShowPhone' | 'isAllowedToShowWhatsapp'
  >
}

interface propsInterface {
  item: itemType
}

const props = defineProps<propsInterface>()

const imgProp: TLazyimg_Props = {
  sources: { url: props.item?.owner?.picture as string },
  alt: props.item?.owner?.name,
}

const languagesList =
  props?.item?.features?.languages?.map(lang => lang.name) ?? []

const calendarUpdate = props?.item?.calendarUpdate
  ? formatCottageCalendarUpdate(props.item.calendarUpdate.daysSinceLastUpdate)
  : ''

const languages = languagesList.length ? addConnectorsToList(languagesList) : ''

const goToCottageContact = routes().cottageContact({
  slug: route.params.slug as string,
  province: route.params.province as string,
})

const goToCottagePhones = routes().cottagePhones({
  slug: route.params.slug as string,
  province: route.params.province as string,
})

const goToCottageContactWhatsApp = routes().cottageContactWhatsApp({
  slug: route.params.slug as string,
  province: route.params.province as string,
})
</script>
